import { inject, Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { LocalStorageService } from '#utils/local-storage/local-storage.service';
import { ChatMessage, ChatSession } from '#interfaces/chat/chat.interface';

@Injectable({ providedIn: 'root' })
export class ChatSignalRService {
    private _storage = inject(LocalStorageService);
    private hubConnection: signalR.HubConnection;

    private _chatSessions = new BehaviorSubject<ChatSession[]>([]);
    private _newMessage = new BehaviorSubject<ChatMessage | null>(null);
    private _typingIndicator = new BehaviorSubject<{
        sessionIdentifier: string;
        senderType: string;
        isTyping: boolean;
    } | null>(null);
    private _isConnected = new BehaviorSubject<boolean>(false);

    get chatSessions$(): Observable<ChatSession[]> {
        return this._chatSessions.asObservable();
    }

    get newMessage$(): Observable<ChatMessage | null> {
        return this._newMessage.asObservable();
    }

    get typingIndicator$(): Observable<any> {
        return this._typingIndicator.asObservable();
    }

    get isConnected$(): Observable<boolean> {
        return this._isConnected.asObservable();
    }

    updateSessions(sessions: ChatSession[]): void {
        this._chatSessions.next(sessions);
    }

    startConnection(): void {
        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(environment.chatSignalRUrl, {
                accessTokenFactory: () =>
                    this._storage.getAccessToken() ?? '',
            })
            .configureLogging(signalR.LogLevel.Warning)
            .withAutomaticReconnect()
            .build();

        this.hubConnection
            .start()
            .then(() => {
                this._isConnected.next(true);
                this.hubConnection.invoke('AdminSubscribe');
            })
            .catch((err) =>
                console.error('Chat SignalR connection error:', err)
            );

        this.hubConnection.onreconnected(() => {
            this._isConnected.next(true);
            this.hubConnection.invoke('AdminSubscribe');
        });

        this.hubConnection.onclose(() => this._isConnected.next(false));

        this.hubConnection.on(
            'NewChatSession',
            (session: ChatSession) => {
                const current = this._chatSessions.value;
                this._chatSessions.next([session, ...current]);
            }
        );

        this.hubConnection.on(
            'ReceiveMessage',
            (message: ChatMessage) => {
                this._newMessage.next(message);
            }
        );

        this.hubConnection.on(
            'SessionUpdated',
            (data: { sessionIdentifier: string; lastMessage: ChatMessage }) => {
                const sessions = this._chatSessions.value.map((s) =>
                    s.sessionIdentifier === data.sessionIdentifier
                        ? {
                              ...s,
                              lastMessagePreview: data.lastMessage.content.substring(0, 200),
                              lastMessageAt: data.lastMessage.sentAt,
                              unreadAdminCount: s.unreadAdminCount + 1,
                          }
                        : s
                );
                this._chatSessions.next(sessions);
            }
        );

        this.hubConnection.on(
            'SessionStatusChanged',
            (data: {
                id: number;
                sessionIdentifier: string;
                status: number;
                adminName?: string;
            }) => {
                if (data.status === 2) {
                    const sessions = this._chatSessions.value.filter(
                        (s) => s.id !== data.id
                    );
                    this._chatSessions.next(sessions);
                } else {
                    const sessions = this._chatSessions.value.map((s) =>
                        s.id === data.id
                            ? {
                                  ...s,
                                  status: data.status,
                                  assignedAdminName: data.adminName || s.assignedAdminName,
                              }
                            : s
                    );
                    this._chatSessions.next(sessions);
                }
            }
        );

        this.hubConnection.on(
            'UserTyping',
            (senderType: string, isTyping: boolean) => {
                this._typingIndicator.next({
                    sessionIdentifier: '',
                    senderType,
                    isTyping,
                });
            }
        );
    }

    joinSession(sessionIdentifier: string): void {
        this.hubConnection?.invoke('AdminJoinSession', sessionIdentifier);
    }

    sendTypingIndicator(
        sessionIdentifier: string,
        isTyping: boolean
    ): void {
        this.hubConnection?.invoke(
            'TypingIndicator',
            sessionIdentifier,
            'admin',
            isTyping
        );
    }

    async closeConnection(): Promise<void> {
        await this.hubConnection?.stop();
    }
}
